import * as d3 from "../d3.js";
import {
    requirePresentationArray,
    requirePresentationFields,
    requirePresentationFinite,
    requirePresentationString,
} from "./calculator-presentation-contract.js";
import { FishyPresentationRenderElement } from "./presentation-render-element.js";
import {
    parseStatBreakdownPresentation,
    setStatBreakdownPresentation,
} from "./stat-breakdown-tooltip.js";
import { calculatorLootRateBasisPresentation } from "./calculator-loot-rate-presentation.js";
import {
    appendCalculatorMixedMetricDecorations,
    calculatorMixedMetricLayout,
    calculatorMetricTextWithUnit,
    fitCalculatorMetricSvgText,
    parseCalculatorMetricUnitMark,
} from "./calculator-metric-unit-mark.js";

const DEFAULT_VIEWBOX_WIDTH = 1351;
const CALLOUT_TOP = 8;
const CALLOUT_LABEL_HEIGHT = 22;
const CALLOUT_METRIC_HEIGHT = 56;
const CALLOUT_METRIC_TOP = CALLOUT_TOP + CALLOUT_LABEL_HEIGHT;
const CALLOUT_HEIGHT = CALLOUT_LABEL_HEIGHT + CALLOUT_METRIC_HEIGHT;
const CALLOUT_RADIUS = 16;
const TRACK_TOP = 150;
const TRACK_HEIGHT = 18;
const TRACK_RADIUS = TRACK_HEIGHT / 2;
const CHART_HEIGHT = TRACK_TOP + TRACK_HEIGHT + 14;
const CALLOUT_GAP_PX = 10;
const TIMELINE_CHART_HEIGHT = 34;
const TIMELINE_TRACK_TOP = 8;
const TIMELINE_TRACK_HEIGHT = 18;
const TIMELINE_TRACK_RADIUS = TIMELINE_TRACK_HEIGHT / 2;

function parseBreakdown(value, path) {
    if (value === null) {
        return null;
    }
    try {
        return parseStatBreakdownPresentation(value);
    } catch (error) {
        if (error && typeof error === "object" && "path" in error) {
            error.path = `${path}${String(error.path).replace("$.statBreakdown", "")}`;
        }
        throw error;
    }
}

function parseDistributionSegment(segment, path) {
    const value = requirePresentationFields(
        segment,
        [
            "breakdown",
            "connectorColor",
            "detailRate",
            "detailRateAriaText",
            "fillColor",
            "label",
            "rowId",
            "strokeColor",
            "textColor",
            "valueAriaText",
            "valueState",
            "valueText",
            "valueUnitMark",
            "weight",
        ],
        path,
    );
    if (!new Set(["known", "unknown", "unavailable"]).has(value.valueState)) {
        throw new TypeError(`${path}.valueState must be known, unknown, or unavailable`);
    }
    return {
        rowId: requirePresentationString(value.rowId, `${path}.rowId`),
        weight: requirePresentationFinite(
            value.weight,
            `${path}.weight`,
            { minimum: 0 },
        ),
        label: requirePresentationString(value.label, `${path}.label`),
        valueState: value.valueState,
        valueText: requirePresentationString(value.valueText, `${path}.valueText`, { allowEmpty: true }),
        valueAriaText: requirePresentationString(
            value.valueAriaText,
            `${path}.valueAriaText`,
        ),
        valueUnitMark: parseCalculatorMetricUnitMark(
            value.valueUnitMark,
            `${path}.valueUnitMark`,
        ),
        detailRate: parseDistributionRateDisplay(value.detailRate, `${path}.detailRate`),
        detailRateAriaText: requirePresentationString(
            value.detailRateAriaText,
            `${path}.detailRateAriaText`,
        ),
        fillColor: requirePresentationString(value.fillColor, `${path}.fillColor`),
        strokeColor: requirePresentationString(value.strokeColor, `${path}.strokeColor`),
        textColor: requirePresentationString(value.textColor, `${path}.textColor`),
        connectorColor: requirePresentationString(value.connectorColor, `${path}.connectorColor`),
        breakdown: parseBreakdown(value.breakdown, `${path}.breakdown`),
    };
}

function parseDistributionRateDisplay(value, path) {
    const display = requirePresentationFields(
        value,
        ["basis", "state", "text", "unitMark"],
        path,
    );
    if (!new Set(["known", "unknown", "unavailable"]).has(display.state)) {
        throw new TypeError(`${path}.state must be known, unknown, or unavailable`);
    }
    const text = requirePresentationString(display.text, `${path}.text`, { allowEmpty: true });
    const unitMark = parseCalculatorMetricUnitMark(display.unitMark, `${path}.unitMark`);
    if (display.state !== "known") {
        if (display.basis !== null || unitMark.kind !== "none") {
            throw new TypeError(`${path} non-known values must not have a unit mark`);
        }
        return { state: display.state, text, basis: null, unitMark };
    }
    const basis = calculatorLootRateBasisPresentation(display.basis);
    if (
        unitMark.kind !== basis.unitMark.kind
        || unitMark.iconId !== basis.unitMark.iconId
        || unitMark.text !== basis.unitMark.text
    ) {
        throw new TypeError(`${path}.unitMark does not match ${basis.basis}`);
    }
    return {
        state: "known",
        text,
        basis: basis.basis,
        unitMark,
    };
}

function parseTimelineSegment(segment, path) {
    const value = requirePresentationFields(
        segment,
        [
            "breakdown",
            "detailText",
            "fillColor",
            "label",
            "segmentId",
            "strokeColor",
            "valueText",
            "weight",
        ],
        path,
    );
    return {
        segmentId: requirePresentationString(value.segmentId, `${path}.segmentId`),
        weight: requirePresentationFinite(
            value.weight,
            `${path}.weight`,
            { minimum: 0 },
        ),
        label: requirePresentationString(value.label, `${path}.label`),
        valueText: requirePresentationString(value.valueText, `${path}.valueText`, { allowEmpty: true }),
        detailText: requirePresentationString(value.detailText, `${path}.detailText`, { allowEmpty: true }),
        fillColor: requirePresentationString(value.fillColor, `${path}.fillColor`),
        strokeColor: requirePresentationString(value.strokeColor, `${path}.strokeColor`),
        breakdown: parseBreakdown(value.breakdown, `${path}.breakdown`),
    };
}

export function parseDistributionChartPresentation(presentation) {
    const value = requirePresentationFields(presentation, ["segments"], "$.distributionChart");
    return {
        segments: requirePresentationArray(value.segments, "$.distributionChart.segments")
            .map((segment, index) => parseDistributionSegment(
                segment,
                `$.distributionChart.segments[${index}]`,
            )),
    };
}

export function parseTimelineChartPresentation(presentation) {
    const value = requirePresentationFields(presentation, ["segments"], "$.timelineChart");
    return {
        segments: requirePresentationArray(value.segments, "$.timelineChart.segments")
            .map((segment, index) => parseTimelineSegment(
                segment,
                `$.timelineChart.segments[${index}]`,
            )),
    };
}

function estimateCalloutWidthPx(label, valueText, detailRate) {
    const longest = Math.max(
        String(label ?? "").length,
        String(valueText ?? "").length,
        calculatorMetricTextWithUnit(detailRate).length,
    );
    return Math.max(112, Math.min(248, 42 + longest * 8.6));
}

function neutralSpan(start, end, leftRadius, rightRadius) {
    const width = Math.max(0, end - start);
    const left = start + Math.min(leftRadius, width / 2);
    const right = end - Math.min(rightRadius, width / 2);
    if (left <= right) {
        return [left, right];
    }
    const center = start + width / 2;
    return [center, center];
}

function polygonPath(points) {
    return points
        .map((point, index) => `${index === 0 ? "M" : "L"} ${point[0]} ${point[1]}`)
        .join(" ")
        .concat(" Z");
}

export class FishyDistributionChart extends FishyPresentationRenderElement {
    static get observedAttributes() {
        return ["aria-label", "viewbox-width"];
    }

    parsePresentation(value) {
        return parseDistributionChartPresentation(value);
    }

    renderPresentation(presentation) {
        const segments = presentation?.segments ?? [];
        if (!segments.length) {
            this.replaceRenderedChildren();
            return;
        }

        const requestedWidth = Number(this.getAttribute("viewbox-width"));
        const width = Number.isFinite(requestedWidth) && requestedWidth > 0
            ? requestedWidth
            : DEFAULT_VIEWBOX_WIDTH;
        const trackWidth = width;
        const totalWeight = segments.reduce((sum, segment) => sum + segment.weight, 0);
        if (totalWeight <= 0) {
            this.replaceRenderedChildren();
            return;
        }
        const x = d3.scaleLinear().domain([0, totalWeight]).range([0, trackWidth]);
        const styles = getComputedStyle(this);
        const trackBackground =
            styles.getPropertyValue("--color-base-300").trim() || "#d6d3d1";
        const trackBorder =
            styles.getPropertyValue("--color-base-content").trim() || "#1f2937";
        const clipId = `distribution-track-${crypto.randomUUID()}`;

        let startPct = 0;
        const provisional = segments.map((segment) => {
            const widthPct = segment.weight;
            const endPct = startPct + widthPct;
            const calloutWidthPx = Math.min(
                width - 8,
                estimateCalloutWidthPx(
                    segment.label,
                    segment.valueText,
                    segment.detailRate,
                ),
            );
            const startX = x(startPct);
            const endX = x(endPct);
            const segmentMidX = startX + (endX - startX) / 2;
            const preferredLeftPx = Math.max(
                0,
                Math.min(width - calloutWidthPx, segmentMidX - calloutWidthPx / 2),
            );
            const current = {
                segment,
                startPct,
                endPct,
                startX,
                endX,
                calloutWidthPx,
                preferredLeftPx,
                calloutLeftPx: preferredLeftPx,
            };
            startPct = endPct;
            return current;
        });

        for (let index = 1; index < provisional.length; index += 1) {
            const previous = provisional[index - 1];
            const current = provisional[index];
            const minimumLeft =
                previous.calloutLeftPx + previous.calloutWidthPx + CALLOUT_GAP_PX;
            current.calloutLeftPx = Math.max(current.preferredLeftPx, minimumLeft);
        }

        let nextLeft = width;
        for (let index = provisional.length - 1; index >= 0; index -= 1) {
            const current = provisional[index];
            const maximumLeft =
                nextLeft - current.calloutWidthPx - (index === provisional.length - 1 ? 0 : CALLOUT_GAP_PX);
            current.calloutLeftPx = Math.max(
                0,
                Math.min(current.calloutLeftPx, maximumLeft),
            );
            nextLeft = current.calloutLeftPx;
        }

        const layout = provisional.map((entry) => ({
            ...entry,
            calloutLeftPx: Math.max(
                0,
                Math.min(width - entry.calloutWidthPx, entry.calloutLeftPx),
            ),
        }));

        const svg = d3
            .create("svg")
            .attr("viewBox", `0 0 ${width} ${CHART_HEIGHT}`)
            .attr("preserveAspectRatio", "xMidYMin meet")
            .attr("role", "img")
            .attr("aria-label", this.getAttribute("aria-label") || "Distribution chart");

        const defs = svg.append("defs");
        defs.append("clipPath")
            .attr("id", clipId)
            .append("rect")
            .attr("x", 0)
            .attr("y", TRACK_TOP)
            .attr("width", trackWidth)
            .attr("height", TRACK_HEIGHT)
            .attr("rx", TRACK_RADIUS)
            .attr("ry", TRACK_RADIUS);

        const connectors = svg.append("g");
        const callouts = svg.append("g");
        const track = svg.append("g");

        layout.forEach((entry, index) => {
            const startX = entry.startX;
            const endX = entry.endX;
            const calloutX = entry.calloutLeftPx;
            const calloutWidth = entry.calloutWidthPx;
            const [segmentNeutralLeft, segmentNeutralRight] = neutralSpan(
                startX,
                endX,
                index === 0 ? TRACK_RADIUS : 0,
                index === layout.length - 1 ? TRACK_RADIUS : 0,
            );
            const [calloutNeutralLeft, calloutNeutralRight] = neutralSpan(
                calloutX,
                calloutX + calloutWidth,
                CALLOUT_RADIUS,
                CALLOUT_RADIUS,
            );

            connectors.append("path")
                .attr(
                    "d",
                    polygonPath([
                        [segmentNeutralLeft, TRACK_TOP],
                        [segmentNeutralRight, TRACK_TOP],
                        [calloutNeutralRight, CALLOUT_TOP + CALLOUT_HEIGHT],
                        [calloutNeutralLeft, CALLOUT_TOP + CALLOUT_HEIGHT],
                    ]),
                )
                .style("fill", entry.segment.connectorColor);

            const callout = callouts.append("g");
            callout.append("rect")
                .attr("x", calloutX)
                .attr("y", CALLOUT_TOP)
                .attr("width", calloutWidth)
                .attr("height", CALLOUT_HEIGHT)
                .attr("rx", CALLOUT_RADIUS)
                .attr("ry", CALLOUT_RADIUS)
                .style("fill", entry.segment.fillColor)
                .style("stroke", entry.segment.strokeColor)
                .style("stroke-width", 1.5);

            appendCalculatorMixedMetricDecorations(callout, {
                x: calloutX,
                y: CALLOUT_METRIC_TOP,
                width: calloutWidth,
                height: CALLOUT_METRIC_HEIGHT,
                color: entry.segment.textColor,
                topUnitMark: entry.segment.valueUnitMark,
                bottomUnitMark: entry.segment.detailRate.unitMark,
            });
            const metricLayout = calculatorMixedMetricLayout({
                x: calloutX,
                width: calloutWidth,
            });

            callout.append("text")
                .attr("x", calloutX + calloutWidth / 2)
                .attr("y", CALLOUT_TOP + 18)
                .attr("text-anchor", "middle")
                .style("fill", entry.segment.textColor)
                .style("font-size", "11px")
                .style("font-weight", "600")
                .text(entry.segment.label);

            const valueDisplay = fitCalculatorMetricSvgText(entry.segment.valueText, {
                fontSize: "15px",
                maximumWidth: metricLayout.contentWidth,
            });
            callout.append("text")
                .attr("x", metricLayout.contentX)
                .attr("y", CALLOUT_METRIC_TOP + CALLOUT_METRIC_HEIGHT * 0.25)
                .attr("text-anchor", "middle")
                .attr("data-calculator-metric-full-text", entry.segment.valueText)
                .style("fill", entry.segment.textColor)
                .style("font-size", valueDisplay.fontSize)
                .style("font-weight", "700")
                .text(valueDisplay.text);

            const rateX = metricLayout.contentX;
            const rateY = CALLOUT_METRIC_TOP + CALLOUT_METRIC_HEIGHT * 0.75;
            const rateText = calculatorMetricTextWithUnit(entry.segment.detailRate);
            const rateDisplay = fitCalculatorMetricSvgText(rateText, {
                fontSize: "11.5px",
                maximumWidth: metricLayout.contentWidth,
            });
            callout.append("text")
                .attr("x", rateX)
                .attr("y", rateY)
                .attr("text-anchor", "middle")
                .attr("data-calculator-loot-rate-value", "")
                .attr("data-calculator-metric-full-text", rateText)
                .style("fill", entry.segment.textColor)
                .style("font-size", rateDisplay.fontSize)
                .style("font-weight", "600")
                .style("font-variant-numeric", "tabular-nums")
                .text(rateDisplay.text);

            if (entry.segment.breakdown) {
                const hotspot = callout.append("rect")
                    .attr("x", calloutX)
                    .attr("y", CALLOUT_TOP)
                    .attr("width", calloutWidth)
                    .attr("height", CALLOUT_HEIGHT)
                    .attr("rx", CALLOUT_RADIUS)
                    .attr("ry", CALLOUT_RADIUS)
                    .attr("class", "distribution-chart__hotspot")
                    .attr("tabindex", 0)
                    .attr("focusable", true)
                    .attr(
                        "aria-label",
                        `${entry.segment.label}. ${entry.segment.valueAriaText}. ${entry.segment.detailRateAriaText}. Show composition.`,
                    )
                    .attr("data-calculator-breakdown-id", entry.segment.breakdown.id)
                    .attr("data-calculator-row-id", entry.segment.rowId)
                    .style("fill", "rgba(255, 255, 255, 0)")
                    .style("pointer-events", "all");
                setStatBreakdownPresentation(hotspot.node(), entry.segment.breakdown);
            }
        });

        track.append("rect")
            .attr("x", 0)
            .attr("y", TRACK_TOP)
            .attr("width", trackWidth)
            .attr("height", TRACK_HEIGHT)
            .attr("rx", TRACK_RADIUS)
            .attr("ry", TRACK_RADIUS)
            .style("fill", trackBackground);

        const segmentsGroup = track.append("g").attr("clip-path", `url(#${clipId})`);
        layout.forEach((entry) => {
            const startX = x(entry.startPct);
            const endX = x(entry.endPct);
            segmentsGroup.append("rect")
                .attr("x", startX)
                .attr("y", TRACK_TOP)
                .attr("width", Math.max(0, endX - startX))
                .attr("height", TRACK_HEIGHT)
                .style("fill", entry.segment.fillColor);
        });

        track.append("rect")
            .attr("x", 0)
            .attr("y", TRACK_TOP)
            .attr("width", trackWidth)
            .attr("height", TRACK_HEIGHT)
            .attr("rx", TRACK_RADIUS)
            .attr("ry", TRACK_RADIUS)
            .style("fill", "none")
            .style("stroke", trackBorder)
            .style("stroke-opacity", 0.1)
            .style("stroke-width", 1.2);

        this.replaceRenderedChildren(svg.node());
    }
}

function timelineSegmentAriaLabel(segment = {}) {
    const label = segment.label;
    const valueText = segment.valueText;
    const detailText = segment.detailText;
    return [label, valueText, detailText, "Show breakdown."]
        .filter(Boolean)
        .join(" ");
}

export class FishyTimelineChart extends FishyPresentationRenderElement {
    static get observedAttributes() {
        return ["aria-label", "viewbox-width"];
    }

    parsePresentation(value) {
        return parseTimelineChartPresentation(value);
    }

    renderPresentation(presentation) {
        const segments = (presentation?.segments ?? [])
            .filter((segment) => segment.weight > 0);
        if (!segments.length) {
            this.replaceRenderedChildren();
            return;
        }

        const requestedWidth = Number(this.getAttribute("viewbox-width"));
        const width = Number.isFinite(requestedWidth) && requestedWidth > 0
            ? requestedWidth
            : DEFAULT_VIEWBOX_WIDTH;
        const totalWidthPct = segments.reduce((sum, segment) => sum + segment.weight, 0);
        const x = d3.scaleLinear().domain([0, totalWidthPct]).range([0, width]);
        const styles = getComputedStyle(this);
        const trackBackground =
            styles.getPropertyValue("--color-base-300").trim() || "#d6d3d1";
        const trackBorder =
            styles.getPropertyValue("--color-base-content").trim() || "#1f2937";
        const clipId = `timeline-track-${crypto.randomUUID()}`;

        const svg = d3
            .create("svg")
            .attr("viewBox", `0 0 ${width} ${TIMELINE_CHART_HEIGHT}`)
            .attr("preserveAspectRatio", "xMidYMin meet")
            .attr("role", "img")
            .attr("aria-label", this.getAttribute("aria-label") || "Fishing timeline");

        const defs = svg.append("defs");
        defs.append("clipPath")
            .attr("id", clipId)
            .append("rect")
            .attr("x", 0)
            .attr("y", TIMELINE_TRACK_TOP)
            .attr("width", width)
            .attr("height", TIMELINE_TRACK_HEIGHT)
            .attr("rx", TIMELINE_TRACK_RADIUS)
            .attr("ry", TIMELINE_TRACK_RADIUS);

        const track = svg.append("g");
        track.append("rect")
            .attr("x", 0)
            .attr("y", TIMELINE_TRACK_TOP)
            .attr("width", width)
            .attr("height", TIMELINE_TRACK_HEIGHT)
            .attr("rx", TIMELINE_TRACK_RADIUS)
            .attr("ry", TIMELINE_TRACK_RADIUS)
            .style("fill", trackBackground);

        const segmentGroup = track.append("g").attr("clip-path", `url(#${clipId})`);
        const hotspots = svg.append("g");

        let startPct = 0;
        segments.forEach((segment, index) => {
            const widthPct = segment.weight;
            const endPct = startPct + widthPct;
            const startX = x(startPct);
            const endX = x(endPct);
            const fillColor = segment.fillColor;
            const strokeColor = segment.strokeColor;

            segmentGroup.append("rect")
                .attr("x", startX)
                .attr("y", TIMELINE_TRACK_TOP)
                .attr("width", Math.max(0, endX - startX))
                .attr("height", TIMELINE_TRACK_HEIGHT)
                .style("fill", fillColor);

            if (index > 0) {
                segmentGroup.append("line")
                    .attr("x1", startX)
                    .attr("x2", startX)
                    .attr("y1", TIMELINE_TRACK_TOP + 2)
                    .attr("y2", TIMELINE_TRACK_TOP + TIMELINE_TRACK_HEIGHT - 2)
                    .style("stroke", strokeColor)
                    .style("stroke-opacity", 0.35)
                    .style("stroke-width", 1);
            }

            if (segment.breakdown) {
                const hotspot = hotspots.append("rect")
                    .attr("x", startX)
                    .attr("y", TIMELINE_TRACK_TOP)
                    .attr("width", Math.max(0, endX - startX))
                    .attr("height", TIMELINE_TRACK_HEIGHT)
                    .attr("rx", Math.min(TIMELINE_TRACK_RADIUS, Math.max(0, endX - startX) / 2))
                    .attr("ry", Math.min(TIMELINE_TRACK_RADIUS, Math.max(0, endX - startX) / 2))
                    .attr("class", "timeline-chart__hotspot distribution-chart__hotspot")
                    .attr("tabindex", 0)
                    .attr("focusable", true)
                    .attr("aria-label", timelineSegmentAriaLabel(segment))
                    .attr("data-calculator-breakdown-id", segment.breakdown.id)
                    .attr("data-calculator-segment-id", segment.segmentId)
                    .style("fill", "rgba(255, 255, 255, 0)")
                    .style("pointer-events", "all");
                setStatBreakdownPresentation(hotspot.node(), segment.breakdown);
            }

            startPct = endPct;
        });

        track.append("rect")
            .attr("x", 0)
            .attr("y", TIMELINE_TRACK_TOP)
            .attr("width", width)
            .attr("height", TIMELINE_TRACK_HEIGHT)
            .attr("rx", TIMELINE_TRACK_RADIUS)
            .attr("ry", TIMELINE_TRACK_RADIUS)
            .style("fill", "none")
            .style("stroke", trackBorder)
            .style("stroke-opacity", 0.12)
            .style("stroke-width", 1.2);

        this.replaceRenderedChildren(svg.node());
    }
}

export function registerDistributionChart() {
    if (window.customElements.get("fishy-distribution-chart")) {
        return;
    }
    window.customElements.define(
        "fishy-distribution-chart",
        FishyDistributionChart,
    );
}

export function registerTimelineChart() {
    if (window.customElements.get("fishy-timeline-chart")) {
        return;
    }
    window.customElements.define("fishy-timeline-chart", FishyTimelineChart);
}
